import { API_BASE_URL } from '../config';
import api from './axiosInstance';

/**
 * Obtiene áreas de negocio paginadas del backend
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {string} search - Término de búsqueda (opcional)
 * @param {boolean} ascending - Ordenar ascendente (true) o descendente (false)
 * @returns {Promise} - { total, businessAreas }
 */
export async function getBusinessAreasPaged(
  page = 1,
  pageSize = 10,
  search = null,
  status = 'Activos',
  ascending = true,
  sortBy = null,
  desc = null
) {
  try {
    const params = {
      page,
      pageSize,
    };

    // status: 'Todos' | 'Activos' | 'Inactivos' or boolean for isEnabled
    if (typeof status === 'boolean') {
      params.isEnabled = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    // Solo agregar search si tiene valor
    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    if (desc !== null && desc !== undefined) {
      params.desc = desc;
    } else {
      params.ascending = ascending;
    }

    const response = await api.get(`${API_BASE_URL}/BusinessAreaSaas`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      total: response.data.total || 0,
      businessAreas: response.data.data || [],
    };
  } catch (error) {
    console.error('getBusinessAreasPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de áreas de negocio.';
    throw error;
  }
}

/**
 * Obtiene un área de negocio por ID
 * @param {number} businessAreaId - ID del área de negocio
 * @returns {Promise} - El área de negocio
 */
export async function getBusinessAreaById(businessAreaId) {
  try {
    const response = await api.get(
      `${API_BASE_URL}/BusinessAreaSaas/${businessAreaId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('getBusinessAreaById failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el área de negocio.';
    throw error;
  }
}

/**
 * Crea un área de negocio nueva
 * @param {object} businessAreaData - Datos del área { name }
 * @returns {Promise}
 */
export async function createBusinessArea(businessAreaData) {
  try {
    const payload = {
      name: businessAreaData.name || '',
    };

    const response = await api.post(
      `${API_BASE_URL}/BusinessAreaSaas`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('createBusinessArea failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo crear el área de negocio.';
    throw error;
  }
}

/**
 * Actualiza un área de negocio existente
 * @param {number} businessAreaId - ID del área de negocio
 * @param {object} businessAreaData - Datos actualizados { name, isEnabled }
 * @returns {Promise}
 */
export async function updateBusinessArea(businessAreaId, businessAreaData) {
  try {
    const payload = {
      name: businessAreaData.name || '',
      isEnabled: businessAreaData.isEnabled ?? true,
    };

    const response = await api.put(
      `${API_BASE_URL}/BusinessAreaSaas/${businessAreaId}`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('updateBusinessArea failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo actualizar el área de negocio.';
    throw error;
  }
}

/**
 * Elimina un área de negocio (borrado lógico)
 * @param {number} businessAreaId - ID del área de negocio a eliminar
 * @returns {Promise}
 */
export async function deleteBusinessArea(businessAreaId) {
  try {
    const response = await api.delete(
      `${API_BASE_URL}/BusinessAreaSaas/${businessAreaId}`,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('deleteBusinessArea failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo eliminar el área de negocio.';
    throw error;
  }
}
